import React, { useEffect } from "react";
import { X, Smartphone, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * Single-shot explainer modal shown the first time a customer is about
 * to be redirected to Fena Pay-by-Bank. Reduces payment abandonment
 * caused by first-time Open Banking users not knowing what's coming.
 *
 * Storage: sets `pg_pbb_explainer_seen=1` in localStorage so we only
 * ever show it once per device. Customers who've seen it before fly
 * straight through to Fena.
 *
 * Props:
 *   open       bool, controls visibility
 *   onConfirm  fn called when user clicks "Continue to Pay" — opens Fena
 *   onClose    fn called when user closes/cancels — stays on checkout
 */
export default function PayByBankExplainerModal({ open, onConfirm, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const confirm = () => {
    try { localStorage.setItem("pg_pbb_explainer_seen", "1"); } catch { /* no-op */ }
    onConfirm?.();
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/85 backdrop-blur-sm overflow-y-auto"
      data-testid="pbb-explainer-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg border-2 rounded-t-sm sm:rounded-sm my-0 sm:my-4 max-h-[92dvh] sm:max-h-[90dvh] flex flex-col overflow-hidden"
        style={{
          borderColor: "rgba(166,255,0,0.35)",
          background: "linear-gradient(135deg, rgba(11,18,32,0.98) 0%, rgba(15,26,42,1) 100%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent stripe */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px] z-10"
          style={{ background: "linear-gradient(90deg, #A6FF00 0%, #5EEAD4 50%, #00C2D6 100%)" }}
        />

        <button
          type="button"
          aria-label="Close"
          className="absolute top-3 right-3 z-20 text-[#9CA8B8] hover:text-[#F7FAFC] transition-colors"
          onClick={onClose}
          data-testid="pbb-explainer-close"
        >
          <X size={20} />
        </button>

        {/* Scrollable content */}
        <div className="p-5 sm:p-8 overflow-y-auto overscroll-contain">
          <div className="pg-mono text-[10px] uppercase tracking-[0.28em] text-[#A6FF00] mb-3">
            // Pay by Bank · 3 quick taps
          </div>
          <h3
            className="text-[#F7FAFC] text-xl sm:text-2xl md:text-3xl leading-tight mb-4"
            style={{ fontFamily: "'Exo 2', sans-serif", fontWeight: 700 }}
          >
            First time using Pay-by-Bank?
          </h3>
          <p className="text-[#9CA8B8] text-sm leading-relaxed mb-5">
            Here's exactly what happens next — it's faster <em>and</em> safer
            than card payments because your details never leave your bank.
          </p>

          <ol className="space-y-3 mb-5">
            <Step n="1" icon={<ArrowRight size={14} />}>
              You tap <strong className="text-[#F7FAFC]">Continue</strong> below — we open your banking app
              (Monzo, Barclays, HSBC, Revolut, Lloyds, etc.)
            </Step>
            <Step n="2" icon={<Smartphone size={14} />}>
              <strong className="text-[#F7FAFC]">Approve in your bank app</strong> with Face ID, fingerprint
              or PIN — just like any other purchase
            </Step>
            <Step n="3" icon={<ShieldCheck size={14} />}>
              You're sent straight back here, <strong className="text-[#F7FAFC]">order confirmed in seconds</strong>.
              Dispatched today if it's before 14:00 GMT.
            </Step>
          </ol>

          <div className="flex items-center gap-2 mb-5 text-[10px] sm:text-[11px] pg-mono uppercase tracking-[0.18em] text-[#5EEAD4]">
            <ShieldCheck size={12} className="shrink-0" />
            <span>FCA-regulated · Bank-grade security</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={confirm}
              className="pg-btn-checkout flex-1"
              data-testid="pbb-explainer-continue"
            >
              Got it — open my bank →
            </button>
            <button
              type="button"
              onClick={onClose}
              className="pg-btn-ghost text-xs sm:flex-none"
              data-testid="pbb-explainer-cancel"
            >
              Cancel
            </button>
          </div>

          <div className="mt-5 pt-4 border-t border-[#1E2E44] text-[11px] text-[#9CA8B8] leading-relaxed">
            Stuck or unsure? Email us at{" "}
            <a href="mailto:info@peptigenlabs.co.uk" className="text-[#5EEAD4] hover:underline">
              info@peptigenlabs.co.uk
            </a>{" "}
            (Mon–Fri, 9 am – 5 pm GMT).
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon, children }) {
  return (
    <li className="flex gap-3">
      <div
        className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center"
        style={{ background: "rgba(166,255,0,0.10)", border: "1px solid rgba(166,255,0,0.35)" }}
      >
        <span className="pg-mono text-xs text-[#A6FF00] font-bold">{n}</span>
      </div>
      <div className="text-sm text-[#9CA8B8] leading-relaxed flex items-center gap-2 flex-wrap">
        <span className="text-[#5EEAD4]">{icon}</span>
        <span>{children}</span>
      </div>
    </li>
  );
}
